import type { ViewController } from '@presentation/shell/NexxusShell';
import { Orchestrator, type OrchestratorResult } from '@domain/qualite/Orchestrator';
import type { AgentLevel, AgentReport } from '@domain/qualite/AgentResult';
import { fmtDate, esc } from '@presentation/shell/format';

const LEVEL_COLOR: Record<AgentLevel, string> = {
  ok: '#2E8B57',
  warn: '#D68910',
  fail: '#C0392B',
};

const CERT_STYLE: Record<
  OrchestratorResult['certificate'],
  { bg: string; fg: string; label: string }
> = {
  'PROD-READY': { bg: '#E3F4EC', fg: '#2E8B57', label: 'PROD-READY' },
  'STAGING-READY': { bg: '#FDF3E1', fg: '#D68910', label: 'STAGING-READY' },
  'NOT-READY': { bg: '#FBE9E7', fg: '#C0392B', label: 'NOT-READY' },
};

function agentCard(report: AgentReport): string {
  const counts = { ok: 0, warn: 0, fail: 0 } as Record<AgentLevel, number>;
  for (const r of report.results) counts[r.level]++;
  const rows = report.results
    .map(
      (
        r,
      ) => `<div style="display:flex;gap:8px;align-items:flex-start;padding:6px 0;border-bottom:1px solid var(--neige);">
      <span style="width:10px;height:10px;border-radius:50%;background:${LEVEL_COLOR[r.level]};flex:0 0 auto;margin-top:4px;"></span>
      <div style="flex:1;"><strong style="font-size:12px;">${esc(r.test)}</strong><br><span style="font-size:12px;color:var(--ardoise);">${esc(r.msg)}</span></div>
    </div>`,
    )
    .join('');
  return `<div class="card">
    <div class="card-title" style="display:flex;justify-content:space-between;align-items:center;">
      <span>${esc(report.agentName)}</span>
      <span style="font-size:11px;font-weight:600;">
        <span style="color:${LEVEL_COLOR.ok};">${counts.ok} OK</span> ·
        <span style="color:${LEVEL_COLOR.warn};">${counts.warn} avert.</span> ·
        <span style="color:${LEVEL_COLOR.fail};">${counts.fail} échec</span>
      </span>
    </div>
    <div style="font-size:11px;color:var(--gris);margin-bottom:8px;">Durée : ${report.durationMs} ms</div>
    ${rows}
  </div>`;
}

function render(section: HTMLElement, res: OrchestratorResult): void {
  const el = section.querySelector<HTMLElement>('#centre-qualite-content');
  if (!el) return;
  const cert = CERT_STYLE[res.certificate];
  el.innerHTML = `
    <div class="kpi-grid">
      <div class="kpi"><div class="label">Certificat</div><div class="value" style="color:${cert.fg};font-size:18px;">
        <span style="display:inline-block;padding:4px 12px;border-radius:999px;background:${cert.bg};">${esc(cert.label)}</span></div></div>
      <div class="kpi"><div class="label">Score global</div><div class="value">${res.scorePct}%</div></div>
      <div class="kpi"><div class="label">Échecs critiques</div><div class="value" style="color:${res.failsCritiques ? LEVEL_COLOR.fail : LEVEL_COLOR.ok};">${res.failsCritiques}</div></div>
      <div class="kpi"><div class="label">Dernière analyse</div><div class="value" style="font-size:14px;">${esc(fmtDate(res.generatedAt))}</div></div>
    </div>
    <div class="grid-2" style="margin-top:16px;">${res.reports.map(agentCard).join('')}</div>`;
}

export const qualiteController: ViewController = async (section, ctx) => {
  const run = async (): Promise<void> => {
    const [salaries, bulletins, ecritures] = await Promise.all([
      ctx.salaries.list(),
      ctx.bulletins.list(),
      ctx.ecritures.list(),
    ]);
    render(section, Orchestrator.run(salaries, bulletins, ecritures));
  };
  await run();

  const w = window as unknown as Record<string, unknown>;
  w['renderCentreQualite'] = () => void run();
};
